import { GridColumnHeader } from '../components/Grid/types';
import type { Attribute, DataSource } from '../types/entities.types';
import { getSearchableFields } from '../utils/searchable.utils';

const gridHeaders: GridColumnHeader[] = [
  {
    name: 'businessMetadata.asset_name',
    label: 'Pipeline',
    width: '25%',
    filter: {
      type: 'text',
      remoteSearchFields: ['asset_name'],
    },
    sortable: true,
    remoteSortField: 'asset_name',
  },
  {
    name: 'technicalMetadata.asset_type',
    label: 'Type',
    width: '20%',
    filter: {
      type: 'aggregation',
      aggregationName: 'asset_type_aggregation',
      remoteSearchFields: ['asset_type'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: true,
    remoteSortField: 'asset_type',
  },
  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    width: '10%',
    filter: {
      type: 'aggregation',
      aggregationName: 'env_aggregation',
      remoteSearchFields: ['env'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: true,
    remoteSortField: 'env',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.job_id',
    label: 'Job Id',
    width: '20%',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.job_name',
    label: 'Job Name',
    width: '20%',
  },
  { name: 'lineage', label: 'Lineage', width: '10%' },
];

const attributesMap: Attribute[] = [
  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    showInColumn: 0,
    description: 'Asset environment',
  },
  {
    name: 'technicalMetadata.asset_type',
    label: 'Asset Type',
    showInColumn: 0,
    description: 'Asset Type',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_altered',
    label: 'Last Altered',
    showInColumn: 0,
    description: 'Last Altered',
    type: 'date',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_altered_age',
    label: 'Last Altered Age',
    showInColumn: 0,
    description: 'Last Altered Age',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_run_status',
    label: 'Last Run Status',
    showInColumn: 0,
    description: 'Last Run Status',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_start_time',
    label: 'Last Start Time',
    showInColumn: 0,
    description: 'Last Start Time',
    type: 'date',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_end_time',
    label: 'Last End Time',
    showInColumn: 0,
    description: 'Last End Time',
    type: 'date',
  },
  {
    name: 'technicalMetadata.usageMetadata.avg_run_time',
    label: 'Avg Run Time',
    showInColumn: 0,
    description: 'Avg Run Time',
  },
  {
    name: 'technicalMetadata.usageMetadata.success_rate',
    label: 'Success Rate',
    showInColumn: 0,
    description: 'Success Rate',
  },
  {
    name: 'technicalMetadata.usageMetadata.failure_rate',
    label: 'Failure Rate',
    showInColumn: 0,
    description: 'Failure Rate',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.workspace_id',
    label: 'Workspace Id',
    showInColumn: 1,
    description: 'Workspace Id',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.databricks_host',
    label: 'Databricks Host',
    showInColumn: 1,
    description: 'Databricks Host',
    type: 'rawLink',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.job_id',
    label: 'Job Id',
    showInColumn: 1,
    description: 'Job Id',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.job_name',
    label: 'Job Name',
    showInColumn: 1,
    description: 'Job Name',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.run_as',
    label: 'Run As',
    showInColumn: 1,
    description: 'Run As',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.trigger_type',
    label: 'Trigger Type',
    showInColumn: 1,
    description: 'Trigger Type',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.paused',
    label: 'Paused',
    showInColumn: 1,
    description: 'Paused',
    type: 'boolean',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.health_rules',
    label: 'Health Rules',
    showInColumn: 1,
    description: 'Health Rules',
    type: 'table',
    tableColumns: ['Metric', 'Operator', 'Value'],
    tableFieldMapping: {
      Metric: 'metric',
      Operator: 'operator',
      Value: 'value',
    },
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.job_url',
    label: 'Job Url',
    showInColumn: 1,
    description: 'Job Url',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.tags',
    label: 'Tags',
    showInColumn: 2,
    description: 'Tags',
    type: 'table',
    tableColumns: ['Name', 'Value'],
    tableFieldMapping: {
      Name: 'name',
      Value: 'value',
    },
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.run_count',
    label: 'Run Count',
    showInColumn: 1,
    description: 'Run Count',
    type: 'string',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.success_count',
    label: 'Success Count',
    showInColumn: 1,
    description: 'Success Count',
    type: 'string',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.failure_count',
    label: 'Failure Count',
    showInColumn: 1,
    description: 'Failure Count',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.last_run_duration_seconds',
    label: 'Last Run Duration Seconds',
    showInColumn: 1,
    description: 'Last Run Duration Seconds',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.last_success_time',
    label: 'Last Success Time',
    showInColumn: 1,
    description: 'Last Success Time',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.timeout_seconds',
    label: 'Timeout Seconds',
    showInColumn: 1,
    description: 'Timeout Seconds',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.properties.failureCheckEnabled',
    label: 'Failure Check Enabled',
    showInColumn: 1,
    description: 'Failure Check Enabled',
  },
  {
    name: 'businessMetadata.asset_id',
    label: 'Asset Id',
    showInColumn: 2,
    description: 'Asset Id',
  },
  {
    name: 'businessMetadata.asset_name',
    label: 'Asset Name',
    showInColumn: 2,
    description: 'Asset Name',
  },
  {
    name: 'businessMetadata.administrativeMetadata.owners',
    label: 'Owners',
    showInColumn: 2,
    description: 'Owners',
    type: 'owners',
  },
  {
    name: 'sourceSpecificMetadata.DatabricksJob.deployment',
    label: 'Deployment',
    showInColumn: 2,
    description: 'Deployment information for Databricks job',
    type: 'dictionary',
  },
];

const customQueryForListPage = `
  DatabricksJob {
    account_id
    workspace_id
    job_id
    job_name
  }
`;

const customQueryForDetailsPage = `
  DatabricksJob {
    databricks_host
    deployment {
      kind
      metadata_file_path
    }
    failure_count
    health_rules {
      metric
      operator
      value
    }
    job_id
    job_name
    job_url
    last_run_duration_seconds
    last_success_time
    paused
    run_as
    run_count
    success_count
    tags {
      name
      value
    }
    timeout_seconds
    trigger_type
    workspace_id
  }
`;

const searchableFields = getSearchableFields(attributesMap);

export const DatabricksJobConfig: DataSource = {
  id: '20',
  name: 'DatabricksJob',
  showLineage: true,
  showLineageTable: true,
  showLineagePipeline: false,
  showLineageQuery: false,
  showLineageCloned: false,
  showLineageCW: false,
  showLineageColumn: false,
  showLineageDashboard: false,
  showColumns: false,
  showPartitionColumns: false,
  gridHeaders,
  attributesMap,
  customQueryForListPage,
  customQueryForDetailsPage,
  searchableFields,
};
